/**
 * FavesTab — Favourites-only filtered view
 *
 * Thin wrapper that filters friends to only show favourited ones.
 */

import { EventEmitter } from '../../../engine/core/EventEmitter';
import { FriendCell } from './FriendCell';
import type { FriendCellData } from './FriendCell';

interface FavesTabEvents {
  'player:select': { playerId: string };
  'player:favourite': { playerId: string; isFavourite: boolean };
  'player:gift': { playerId: string };
}

export class FavesTab extends EventEmitter<FavesTabEvents> {
  public element: HTMLElement;
  private listEl: HTMLElement;
  private emptyEl: HTMLElement;
  private cells: Map<string, FriendCell> = new Map();
  private friends: FriendCellData[] = [];

  constructor() {
    super();
    this.element = document.createElement('div');
    this.element.className = 'crew-tab-content';

    this.listEl = document.createElement('div');
    this.listEl.className = 'crew-friend-list';

    this.emptyEl = document.createElement('div');
    this.emptyEl.className = 'crew-empty-state';
    this.emptyEl.innerHTML = `
      <div class="crew-empty-icon">💜</div>
      <div class="crew-empty-title">NO FAVES YET</div>
      <div class="crew-empty-text">Tap the heart on a friend to add them here</div>
    `;

    this.element.appendChild(this.listEl);
    this.element.appendChild(this.emptyEl);
  }

  setFriends(friends: FriendCellData[]): void {
    this.friends = friends;
    this.renderList();
  }

  updateFavourite(playerId: string, isFavourite: boolean): void {
    const friend = this.friends.find(f => f.id === playerId);
    if (friend) {
      friend.isFavourite = isFavourite;
      this.renderList();
    }
  }

  /** Optimistic daily-gift send: cooldown state + coin flyout on the cell. */
  markGifted(playerId: string): void {
    const friend = this.friends.find(f => f.id === playerId);
    if (friend) friend.giftAvailable = false;
    this.cells.get(playerId)?.markGifted();
  }

  /** Re-arm (or disarm) a friend's gift button after a server verdict. */
  setGiftAvailable(playerId: string, available: boolean): void {
    const friend = this.friends.find(f => f.id === playerId);
    if (friend) friend.giftAvailable = available;
    this.cells.get(playerId)?.setGiftAvailable(available);
  }

  updateOnlineStatus(playerId: string, online: boolean): void {
    const friend = this.friends.find(f => f.id === playerId);
    if (friend) {
      friend.isOnline = online;
      const cell = this.cells.get(playerId);
      if (cell) cell.update({ isOnline: online });
    }
  }

  destroy(): void {
    this.cells.forEach(c => c.destroy());
    this.cells.clear();
    this.element.remove();
    this.removeAllListeners();
  }

  private renderList(): void {
    this.cells.forEach(c => c.destroy());
    this.cells.clear();
    this.listEl.innerHTML = '';

    const faves = this.friends.filter(f => f.isFavourite);

    this.emptyEl.style.display = faves.length === 0 ? 'flex' : 'none';
    this.listEl.style.display = faves.length === 0 ? 'none' : 'flex';

    for (const friend of faves) {
      const cell = new FriendCell(friend);
      cell.on('select', (data) => this.emit('player:select', data));
      cell.on('favourite', (data) => this.emit('player:favourite', data));
      cell.on('gift', (data) => this.emit('player:gift', data));
      this.cells.set(friend.id, cell);
      this.listEl.appendChild(cell.element);
    }
  }
}
